import { Injectable, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

export type ReportExportFormat = 'CSV' | 'EXCEL';

/** One parameter as the embedding SDK's getParameters() returns it. */
export interface ReportExportParameter {
  Name: string;
  Values: Array<string | number>;
}

export interface ReportExportRequest {
  dashboardId: string;
  contentId?: string;
  sheetId?: string;
  format: ReportExportFormat;
  parameters: ReportExportParameter[];
}

export interface ReportExportStatus {
  exportId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED';
  format?: ReportExportFormat;
  pollAfterSeconds?: number;
  elapsedSeconds?: number;
  fileName?: string;
  downloadUrl?: string;
  expiresInSeconds?: number;
  errorCode?: string;
  message?: string;
}

/**
 * Export CSV and Excel from the report viewer page (ARAP-1889). The backend runs the export as the
 * signed-in user's own QuickSight identity, so their row level security applies to the file.
 * Internal users are routed to /internal/user/reports/export by the auth interceptor.
 */
@Injectable({
  providedIn: 'root'
})
export class ReportExportService {
  private API_URL: string;

  constructor(private http: HttpClient, @Inject('API_URL') apiUrl: string) {
    this.API_URL = apiUrl;
  }

  /** Starts an export of the report's table with the given selections. */
  startExport(request: ReportExportRequest): Observable<ReportExportStatus> {
    return this.http.post<ReportExportStatus>(`${this.API_URL}/user/reports/export`, request);
  }

  /** Reads the status of one of the user's exports; COMPLETED includes a short-lived download link. */
  getExport(exportId: string): Observable<ReportExportStatus> {
    return this.http.get<ReportExportStatus>(`${this.API_URL}/user/reports/export`, { params: { exportId } });
  }
}
